/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/

import { inject, Injectable } from "@angular/core";
import { forkJoin, Observable, of } from "rxjs";
import { first, map } from "rxjs/operators";
import { AbstractObjectList } from "@src/app/model/abstract-object";
import { User } from "@src/app/model/user";
import { CurrentUserService } from "@src/app/modules/user/current-user.service";
import { BatchType, ImportParser } from '@src/app/modules/importer/import-page/Import-parser-interface';
import { ImportDoc } from "@src/app/modules/importer/ImportDoc";
import { GeneralParser } from "@src/app/modules/importer/General/GeneralParserService";
import { ParseOptionBase, ParseValidator } from "@src/app/modules/importer/General/ParseOptionBase";
import { ParseOptionBaseMapped } from "@src/app/modules/importer/General/ParseOptionBaseMapped";
import { POBValidators, validateDatePOB, validateEmailPOB } from "@src/app/modules/importer/General/POBValidators";
import { Unit } from "@src/app/model/unit";
import { UnitService } from "@src/app/modules/unit/unit.service";
import { ImportRow } from "@src/app/modules/importer/ImportRow";
import { RoleService } from "@src/app/pages/role-page/role.service";
import { Role } from "@src/app/model/role";
import { PersonService } from "@src/app/pages/person-page/person.service";
import { PersonUnitRole } from "@src/app/model/person-unit-role";
import { TenantService } from '@src/app/shared/tenant.service';
import { HttpClient } from "@angular/common/http";
import { MyInjector } from "@src/app/app.module";
import { ContactTypes, EntityTypes, Person } from "@src/app/model/person";
import { ImportField } from "@src/app/modules/importer/ImportField";

@Injectable({
    providedIn: 'root'
})
export class PeopleParserService implements ImportParser {

    gps = new GeneralParser();
    unitSvc = inject(UnitService);
    currentUserSvc = inject(CurrentUserService);
    roleSvc = inject(RoleService);
    personSvc = inject(PersonService);

    batchType: BatchType = 'Person';

    units : Unit[] = [];
    people: Person[] = [];

    importDoc: ImportDoc;
    currentUser: User;

    personIdPob = new ParseOptionBase('PersonId', 'id', [POBValidators.requiredPOB(), this.requiredPersonId()]);
    unitNamePOB = new ParseOptionBaseMapped('Unit', 'unitId', [POBValidators.requiredPOB()]).makeSilent();
    titlePOB = new ParseOptionBase('Title', 'title');
    firstNamePOB = new ParseOptionBase('First Name', 'firstName');
    lastNamePOB = new ParseOptionBase('Person Last Name', 'lastName', [POBValidators.requiredPOB()]);
    emailPOB = new ParseOptionBase('Email', 'email', [validateEmailPOB()]);
    phonePOB = new ParseOptionBase('Phone', 'phone');
    addressPOB = new ParseOptionBase('Address', 'address');
    notesPOB = new ParseOptionBase('Notes', 'notes');
    rolePOB = new ParseOptionBaseMapped('Role', 'roleId', [POBValidators.requiredPOB()]);
    fromDtPob = new ParseOptionBase('From Date', 'startDate', [validateDatePOB(true)]);
    toDtPob = new ParseOptionBase('To Date', 'endDate', [validateDatePOB(true)]);
    entityPob = new ParseOptionBaseMapped('Entity Type', 'entityType');
    contactTypePob = new ParseOptionBaseMapped('Contact Type', 'contactType');
    integrationIdPOB = new ParseOptionBase('IntegrationId', 'integrationId');

    requiredPersonId() {
        const pobValidator = new ParseValidator();

        pobValidator.validateRow = (field: ImportField) => {
            const val = field.getValue();
            const p = this.people.find( u => u.id === val);
            if (p) {
                return null;
            }
            return $localize ` PersonID does not exist`
        }

        return pobValidator;
    }    

    availableImportItems = [
        this.unitNamePOB,
        this.titlePOB,
        this.firstNamePOB,
        this.lastNamePOB,
        this.emailPOB,
        this.phonePOB,
        this.addressPOB,
        this.notesPOB,
        this.rolePOB,
        this.fromDtPob,
        this.toDtPob,
        this.entityPob,
        this.contactTypePob,
        this.personIdPob,
        this.integrationIdPOB,
    ];

    setUp() {
        this.importDoc = new ImportDoc();

        return forkJoin({
            currentUser: this.currentUserSvc.getCurrentUser().pipe(first()),
            units: this.unitSvc.get(true),
            roles: this.roleSvc.get(true),
            people: this.personSvc.get<Person>(true),
        }).pipe(map(result => {
            this.currentUser = result.currentUser;
            this.unitNamePOB.setOptions(result.units, this.importDoc);
            this.rolePOB.setOptions((result.roles as Role[]).filter( r => r.unitLinked), this.importDoc);
            this.entityPob.setOptions(EntityTypes.items,this.importDoc);
            this.contactTypePob.setOptions(ContactTypes.omcSelectables(), this.importDoc);
            this.people = result.people;
            return true;
        }));
    }

    parseRows(rows: unknown[][]): ImportDoc {
        this.gps.availableImportItems = this.availableImportItems;
        this.gps.parseRows(rows, this.importDoc);
        return this.importDoc;
    }

    integrationIds: Record<string, string> = {};
    validateRow = (row: ImportRow, val: PersonUnitRole) => {
        const intId = val.integrationId;
        if (intId) {
            const existing = this.integrationIds[intId];
            if (existing && existing !== val.personEmail) {
                row.addError('integrationId was already used');
            } else {
                this.integrationIds[intId] = val.personEmail;
            }
        }
        if (val.roleId || val.unitId || val.startDate || val.endDate) {
            if (!val.roleId) {
                row.addError('roleId is required if unit or effective dates are provided');
            }
            if (!val.unitId) {
                row.addError('unitId is required if role or effective dates are provided');
            }
        }
    }

    postToServer(): Observable<AbstractObjectList> {
        this.integrationIds = {};
        const postItems = this.gps.getItemsToPost({validateItemFn: this.validateRow.bind(this)});
        console.log(postItems);
        if (this.importDoc.hasErrors()) {
            return of(null);
        } else {
            const postData = {
                people: postItems,
            }
            const url = TenantService.ajaxPath + 'importFile/' + this.batchType;
            const http = MyInjector.instance.get(HttpClient);
            return http.post<AbstractObjectList>(url, postData);
        }

    }
}
